import { SUBSCRIPTION_CART_STORAGE_KEY } from "./subscriptionCart";
import { markAdminDevice } from "./installProfile";
import { revokeServerSession } from "./serverSessionLogout";

export const AUTH_SESSION_CHANGED_EVENT = "agiliza:auth-session-changed";
export const AUTH_SESSION_ID_STORAGE_KEY = "agiliza:auth-session-id";

function createAuthSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function saveAuthSessionId() {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(AUTH_SESSION_ID_STORAGE_KEY, createAuthSessionId());
  } catch {
    /* sessionStorage indisponível: a sessão ainda segue funcional */
  }
}

export function getAuthSessionId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage.getItem(AUTH_SESSION_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearAdminSessionState() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem("selected-establishment-id");
    // Carrinho de assinatura é por dispositivo: limpar ao sair/trocar de conta
    // evita que a próxima conta veja itens adicionados por outro usuário.
    window.localStorage.removeItem(SUBSCRIPTION_CART_STORAGE_KEY);
    window.sessionStorage.removeItem("establishment-cache");
    window.sessionStorage.removeItem("establishment-list-cache");
    window.sessionStorage.removeItem(AUTH_SESSION_ID_STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

function notifyAuthSessionChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

// helpers de cookie local (não httpOnly) mantidos como fallback/compat
function setCookie(name: string, value: string, days = 7) {
  if (typeof document === "undefined") return;
  const maxAge = days * 24 * 60 * 60;
  const isSecure = typeof location !== "undefined" && location.protocol === "https:";
  const secureAttr = isSecure ? "; secure" : "";
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/; max-age=${maxAge}; samesite=lax${secureAttr}`;
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  const isSecure = typeof location !== "undefined" && location.protocol === "https:";
  const secureAttr = isSecure ? "; secure" : "";
  document.cookie = `${name}=; path=/; max-age=0; samesite=lax${secureAttr}`;
}

/**
 * Salva o token localmente e dispara requisição para rota de API que grava o
 * cookie httpOnly. O backend só lerá esse cookie na próxima requisição.
 */
export async function saveToken(token: string) {
  if (typeof window === "undefined") return;
  clearAdminSessionState();
  localStorage.setItem("auth_token", token);
  saveAuthSessionId();

  // Este dispositivo e de administrador: destrava o app caso ele tenha sido
  // marcado como app de cliente por uma visita a uma pagina de marca ou a area
  // do cliente. O perfil sobrevive ao logout de proposito, para que o proximo
  // acesso a /login nao seja redirecionado de volta. Ver lib/installProfile.ts.
  markAdminDevice();

  try {
    await fetch("/api/auth/set-cookie", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      // esse endpoint roda no mesmo domínio, não precisa de credentials
    });
  } catch {
    // ignorar falhas; cookie não será httpOnly mas o `setCookie` abaixo
    // garante algo funcional
    setCookie("auth_token", token, 7);
  } finally {
    notifyAuthSessionChanged();
  }
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("auth_token");
}

/**
 * Limpa token do localStorage, apaga o cookie httpOnly e revoga o token no
 * servidor (best-effort: a sessão local cai mesmo se o servidor falhar).
 */
export async function clearToken() {
  if (typeof window === "undefined") return;
  const token = getToken();
  const revokePushBinding = token
    ? import("./pushNotifications")
        .then(({ revokeCurrentAdminPushBinding }) => revokeCurrentAdminPushBinding(token))
        .catch(() => undefined)
    : Promise.resolve();
  localStorage.removeItem("auth_token");
  clearAdminSessionState();
  deleteCookie("auth_token");
  try {
    await Promise.allSettled([
      revokePushBinding,
      token ? revokeServerSession("/auth/logout", token) : Promise.resolve(),
      fetch("/api/auth/clear-cookie", { method: "POST" }),
    ]);
  } catch {
    /* swallow */
  } finally {
    notifyAuthSessionChanged();
  }
}
